import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePublicConfigContext } from '@/core/contexts/PublicConfigContext';
import { env } from '@/core/config/env';

const CONSENT_STORAGE_KEY = 'tracking_consent';

export type ConsentStatus = 'accepted' | 'rejected' | null;

/**
 * Get current tracking consent status
 */
export function getTrackingConsent(): ConsentStatus {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
  if (stored === 'true') return 'accepted';
  if (stored === 'false') return 'rejected';
  return null;
}

/**
 * Check if tracking is allowed (consent given or banner disabled)
 * Can be called with explicit config or will use context
 */
export function isTrackingAllowed(consentBannerEnabled?: boolean): boolean {
  // Use provided value or fallback to env (for backwards compatibility)
  const enabled = consentBannerEnabled !== undefined 
    ? consentBannerEnabled 
    : env.enableConsentBanner;
  
  // If consent banner is disabled, always allow tracking
  if (!enabled) return true;
  
  // Otherwise, check for explicit consent
  return getTrackingConsent() === 'accepted';
}

/**
 * Cookie consent banner component
 * Only shows if VITE_ENABLE_CONSENT_BANNER=true and user hasn't made a choice
 */
export function CookieBanner() {
  const { t } = useTranslation();
  const { config: publicConfig, isLoading } = usePublicConfigContext();
  const [visible, setVisible] = useState(false);

  // Use config from context or fallback to env
  const consentBannerEnabled = publicConfig?.consentBannerEnabled ?? env.enableConsentBanner;

  useEffect(() => {
    // Wait for config to load
    if (isLoading) return;
    
    // Only show if consent banner is enabled and user hasn't made a choice
    if (consentBannerEnabled && getTrackingConsent() === null) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, consentBannerEnabled]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
    setVisible(false);
    // Reload to initialize tracking with consent
    window.location.reload();
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'false');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white dark:bg-secondary-800 border-t border-secondary-200 dark:border-white/10 shadow-lg">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex-1 text-sm text-secondary-600 dark:text-secondary-400">
          <p>
            {t(
              'consent.message',
              'We use cookies and similar technologies to improve your experience, analyze traffic, and personalize content. By clicking "Accept", you consent to our use of cookies.'
            )}
          </p>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary-400 hover:text-indigo-500 underline"
          >
            {t('consent.learnMore', 'Learn more')}
          </a>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReject}
            className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-300 bg-secondary-100 dark:bg-secondary-800 rounded-md hover:bg-secondary-200 dark:bg-secondary-700 transition-colors"
          >
            {t('consent.reject', 'Reject')}
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
          >
            {t('consent.accept', 'Accept')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;


